import { useEffect, useMemo, useState } from 'react';

import { faFlag, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import routes from '@/routes/routes.constants';

import type {
  InsuranceStatusCtaValue,
  StatusTypeValue,
} from '../../../@types/zuub/contracts';
import type { TableColumn } from '../../../common-components/composed/sortableTable/SortableTable';
import { SortableTable } from '../../../common-components/composed/sortableTable/SortableTable';
import { StatusChip } from '../../../common-components/elements/StatusChip/StatusChip';
import { useDevtoolsShortcut } from '../../../utils/hooks/useDevtoolsShortcut';
import { toBusinessName } from '../../../utils/string';
import { useLocalizedFormatDate } from '../../claims/freeClaims/state';
import { selectedPayerIdAtom } from '../atoms';
import type { AutoVerifyTableRow } from '../helpers';

import { ActionsColumn } from './ActionsColumn';
import { EnableAdditionalBenefitsInfoHoverCard } from './EnableAdditionalBenefitsInfoHoverCard';

// Map CTA code to display string
const getInsuranceStatusCtaDisplay = (cta: InsuranceStatusCtaValue): string => {
  switch (cta) {
    case 'ACTIVE':
      return 'Active';
    case 'INACTIVE':
      return 'Inactive';
    case 'CHECK_MANUALLY':
      return 'Check manually';
    case 'CHECK_VIDEA_LOGIN':
      return 'Check Videa log-in';
    case 'CONFIRM_PATIENT_DETAILS':
      return 'Confirm patient details';
    case 'NOT_ELIGIBLE':
      return 'Not Eligible';
    case 'CONFIRM_NPI_NUMBER':
      return 'Confirm NPI number';
    case 'NO_PAYER_LISTED':
      return 'No Payer Listed';
    default:
      return cta;
  }
};

// Map statusType to StatusChip variant
const getStatusTypeVariant = (
  statusType: StatusTypeValue,
): 'success' | 'warning' | 'error' | 'default' => {
  switch (statusType) {
    case 'success':
      return 'success';
    case 'warn':
      return 'warning';
    case 'error':
      return 'error';
    case 'action':
      return 'default'; // Light grey background
    default:
      return 'default';
  }
};

type SortField =
  | 'flag'
  | 'patient'
  | 'appointment'
  | 'payerName'
  | 'insuranceStatus'
  | 'remainingBenefits'
  | 'deductible'
  | 'lastRun';
type SortDirection = 'asc' | 'desc';

interface AutoVerifyTableProps {
  rows: AutoVerifyTableRow[];
  isSearchActive?: boolean;
}

export const AutoVerifyTable = ({
  rows,
  isSearchActive = false,
}: AutoVerifyTableProps) => {
  // state
  const [sortField, setSortField] = useState<SortField>('appointment');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [devInfoModalData, setDevInfoModalData] = useState<
    Record<string, unknown>[] | null
  >(null);

  // atoms
  const setSelectedPayerIdAtom = useSetAtom(selectedPayerIdAtom);

  // hooks
  const formatLocalizedDate = useLocalizedFormatDate();
  const history = useHistory();
  const { showDevtools } = useDevtoolsShortcut();
  const isPrivacyOn = useSelector((state: any) => state.login?.user?.privacyOn);

  // Sort rows based on current sort settings
  // For multi-plan rows, use the primary plan (order 0) or first plan for comparison
  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      let comparison = 0;

      // Get primary plan (order 0) or first plan for comparison
      const planA = a.plans.find((p) => p.order === 0) || a.plans[0];
      const planB = b.plans.find((p) => p.order === 0) || b.plans[0];

      switch (sortField) {
        case 'appointment': {
          const dateA = a.appointment?.appointment.start || '';
          const dateB = b.appointment?.appointment.start || '';
          comparison = dateA.localeCompare(dateB);
          break;
        }
        case 'patient': {
          const nameA = (planA?.patientDisplayName || '').toLowerCase();
          const nameB = (planB?.patientDisplayName || '').toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;
        }
        case 'payerName': {
          // Sort by first payer name alphabetically
          const payerA = (planA?.payerName || '').toLowerCase();
          const payerB = (planB?.payerName || '').toLowerCase();
          comparison = payerA.localeCompare(payerB);
          break;
        }
        case 'insuranceStatus': {
          const statusA = planA?.insuranceStatusV2 || '';
          const statusB = planB?.insuranceStatusV2 || '';
          comparison = statusA.localeCompare(statusB);
          break;
        }
        case 'remainingBenefits': {
          const benefitsA = planA?.remainingBenefits || 0;
          const benefitsB = planB?.remainingBenefits || 0;
          comparison = benefitsA - benefitsB;
          break;
        }
        case 'deductible': {
          const deductibleA = planA?.remainingDeductible || 0;
          const deductibleB = planB?.remainingDeductible || 0;
          comparison = deductibleA - deductibleB;
          break;
        }
        case 'lastRun': {
          const lastRunA = a.zuubPatient.lastRun || '';
          const lastRunB = b.zuubPatient.lastRun || '';
          comparison = lastRunA.localeCompare(lastRunB);
          break;
        }
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [rows, sortField, sortDirection]);

  const handleSort = (field: string, direction: SortDirection) => {
    setSortField(field as SortField);
    setSortDirection(direction);
  };

  // Define columns for SortableTable
  const columns: TableColumn<AutoVerifyTableRow>[] = [
    {
      key: 'flag',
      header: '',
      width: 'vai-w-[50px]',
      render: (row) => {
        // Show flag if any plan needs payer portal connection
        const shouldShowInfoIcon = row.plans.some(
          (plan) => plan.needsPayerPortalConnection,
        );
        // Use the first plan that needs connection, or first plan
        const planWithConnection =
          row.plans.find((plan) => plan.needsPayerPortalConnection) ||
          row.plans[0];

        return (
          <EnableAdditionalBenefitsInfoHoverCard
            isOpen={shouldShowInfoIcon}
            onSetUpCredentials={() => {
              const payerId = planWithConnection?.payerId;
              if (payerId) {
                setSelectedPayerIdAtom(payerId);
                history.push(routes.CONNECT_PAYERS);
              }
            }}
          />
        );
      },
    },
    {
      key: 'appointment',
      header: <Trans>Appointment</Trans>,
      width: 'vai-w-[140px]',
      isSortable: true,
      render: (row) => {
        if (!row.appointment) {
          return <>—</>;
        }
        return (
          <>
            {isSearchActive
              ? formatLocalizedDate(
                  row.appointment.appointment.start,
                  false,
                  'P',
                )
              : formatLocalizedDate(
                  row.appointment.appointment.start,
                  false,
                  'h:mm a',
                )}
          </>
        );
      },
    },
    {
      key: 'patient',
      header: <Trans>Patient</Trans>,
      width: 'vai-w-[180px]',
      isSortable: true,
      render: (row) => {
        const name =
          row.plans[0]?.patientDisplayName || row.zuubPatient.patientName;

        if (isPrivacyOn) {
          return (
            <span className="vai-font-semibold vai-text-gray-900 vai-capitalize">
              {name}
            </span>
          );
        }

        // Privacy mode OFF: show blurred initials
        const initials = name.split(' ').map((part) => part[0] || '');
        return (
          <span className="vai-font-semibold vai-text-gray-700 vai-inline-flex vai-flex-wrap">
            {initials.map((initial, i) => (
              <span key={i} className="vai-inline-block">
                <span>{initial}</span>
                <span className="vai-text-gray-300 vai-blur-[5px] vai-select-none">
                  ███
                </span>
              </span>
            ))}
          </span>
        );
      },
    },
    {
      key: 'payerName',
      header: <Trans>Payer Name</Trans>,
      width: 'vai-w-[200px]',
      isSortable: true,
      render: (row) => {
        return (
          <div className="vai-flex vai-flex-col vai-py-2">
            {row.plans.map((planData, index) => {
              const payerName = planData.payerName;
              return (
                <div
                  key={`${planData.payerId}-${planData.order}`}
                  className="vai-flex vai-flex-col vai-min-h-[40px] vai-justify-center vai-mb-2 last:vai-mb-0"
                >
                  <span>{payerName ? toBusinessName(payerName) : '—'}</span>
                  {showDevtools && (
                    <div className="vai-flex vai-flex-wrap vai-gap-1 vai-mt-1">
                      {planData.verificationStatus && (
                        <span className="vai-text-xs vai-px-1.5 vai-py-0.5 vai-bg-purple-100 vai-text-purple-700 vai-rounded">
                          {planData.verificationStatus}
                        </span>
                      )}
                      {planData.resultSource && (
                        <span className="vai-text-xs vai-px-1.5 vai-py-0.5 vai-bg-gray-200 vai-text-gray-700 vai-rounded">
                          {planData.resultSource}
                        </span>
                      )}
                      {planData.source && (
                        <span className="vai-text-xs vai-px-1.5 vai-py-0.5 vai-bg-blue-100 vai-text-blue-700 vai-rounded">
                          {planData.source}
                        </span>
                      )}
                      {planData.mismatches && planData.mismatches.length > 0 && (
                        <button
                          onClick={() => setDevInfoModalData(planData.mismatches)}
                          className="vai-text-gray-500 hover:vai-text-gray-700"
                        >
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            className="vai-w-4 vai-h-4"
                          />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      key: 'insuranceStatus',
      header: <Trans>Insurance Status</Trans>,
      width: 'vai-w-[180px]',
      isSortable: true,
      render: (row) => {
        return (
          <div className="vai-flex vai-flex-col vai-py-2">
            {row.plans.map((planData) => {
              const insuranceStatusV2 = planData.insuranceStatusV2;
              const statusType = planData.statusType;

              if (!insuranceStatusV2) {
                return (
                  <div
                    key={`${planData.payerId}-${planData.order}`}
                    className="vai-flex vai-items-center vai-min-h-[40px] vai-mb-2 last:vai-mb-0"
                  >
                    <StatusChip variant="ghost" text="Unknown" />
                  </div>
                );
              }

              const variant = statusType
                ? getStatusTypeVariant(statusType)
                : 'default';

              return (
                <div
                  key={`${planData.payerId}-${planData.order}`}
                  className="vai-flex vai-items-center vai-min-h-[40px] vai-mb-2 last:vai-mb-0"
                >
                  <StatusChip
                    variant={variant}
                    text={getInsuranceStatusCtaDisplay(insuranceStatusV2)}
                  />
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      key: 'remainingBenefits',
      header: <Trans>Remaining Benefits</Trans>,
      width: 'vai-w-[150px]',
      isSortable: true,
      render: (row) => {
        return (
          <div className="vai-flex vai-flex-col vai-py-2">
            {row.plans.map((planData) => {
              const remainingBenefits = planData.remainingBenefits;

              // Don't show if status is not active or if no benefit data
              if (
                planData.insuranceStatusV2 !== 'ACTIVE' ||
                remainingBenefits === undefined ||
                remainingBenefits === null
              ) {
                return (
                  <div
                    key={`${planData.payerId}-${planData.order}`}
                    className="vai-flex vai-items-center vai-min-h-[40px] vai-mb-2 last:vai-mb-0"
                  >
                    —
                  </div>
                );
              }

              // Show flag icon if remaining benefits are 0
              const hasNoRemainingBenefits = remainingBenefits === 0;

              return (
                <div
                  key={`${planData.payerId}-${planData.order}`}
                  className="vai-flex vai-items-center vai-gap-2 vai-min-h-[40px] vai-mb-2 last:vai-mb-0"
                >
                  <span>
                    $
                    {remainingBenefits.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                  {hasNoRemainingBenefits && (
                    <FontAwesomeIcon
                      icon={faFlag}
                      className="vai-text-red-500 vai-w-4 vai-h-4"
                      title="No remaining benefits"
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      key: 'deductible',
      header: <Trans>Deductible</Trans>,
      width: 'vai-w-[130px]',
      isSortable: true,
      render: (row) => {
        return (
          <div className="vai-flex vai-flex-col vai-py-2">
            {row.plans.map((planData) => {
              const remainingDeductible = planData.remainingDeductible;
              const totalDeductible = planData.totalDeductible;

              // Don't show if status is not active or if no deductible data
              if (
                planData.insuranceStatusV2 !== 'ACTIVE' ||
                remainingDeductible === undefined ||
                remainingDeductible === null
              ) {
                return (
                  <div
                    key={`${planData.payerId}-${planData.order}`}
                    className="vai-flex vai-items-center vai-min-h-[40px] vai-mb-2 last:vai-mb-0"
                  >
                    —
                  </div>
                );
              }

              // Check if deductible is fully met
              const isDeductibleMet = remainingDeductible === 0;

              // Handle "No Deductible" case when totalDeductible is 0
              if (totalDeductible === 0) {
                return (
                  <div
                    key={`${planData.payerId}-${planData.order}`}
                    className="vai-flex vai-items-center vai-text-gray-500 vai-min-h-[40px] vai-mb-2 last:vai-mb-0"
                  >
                    <Trans>No Deductible</Trans>
                  </div>
                );
              }

              return (
                <div
                  key={`${planData.payerId}-${planData.order}`}
                  className="vai-flex vai-items-center vai-gap-2 vai-min-h-[40px] vai-mb-2 last:vai-mb-0"
                >
                  <span>
                    $
                    {remainingDeductible.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                    })}
                  </span>
                  {isDeductibleMet && (
                    <span className="vai-text-green-600" title="Deductible met">
                      ✓
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      key: 'lastRun',
      header: <Trans>Last Run</Trans>,
      width: 'vai-w-[120px]',
      isSortable: true,
      render: (row) => {
        const lastRun = row.zuubPatient.lastRun;
        return <>{lastRun ? formatLocalizedDate(lastRun) : '—'}</>;
      },
    },
    {
      key: 'actions',
      header: <Trans>Actions</Trans>,
      width: 'vai-w-[150px]',
      render: (row) => {
        return (
          <ActionsColumn
            appointment={row.appointment}
            zuubPatient={row.zuubPatient}
            onManagePayerConnection={(payerId) => {
              setSelectedPayerIdAtom(payerId);
              history.push(routes.CONNECT_PAYERS);
            }}
          />
        );
      },
    },
  ];

  // When searching, default to descending order for appointment dates
  useEffect(() => {
    if (
      isSearchActive &&
      sortField === 'appointment' &&
      sortDirection === 'asc'
    ) {
      setSortDirection('desc');
    }
  }, [isSearchActive, sortField, sortDirection]);

  return (
    <>
      <SortableTable
        data={sortedRows}
        columns={columns}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        rowKey={(row) =>
          `auto-verify-${row.plans[0]?.unifiedAppointmentId || row.zuubPatient.unifiedAppointmentId}-${row.plans[0]?.pmsPatientId || row.zuubPatient.pmsPatientId}`
        }
        className="vai-h-full"
      />

      {/* Dev Info Modal */}
      <Dialog
        open={devInfoModalData !== null}
        onOpenChange={(isOpen) => !isOpen && setDevInfoModalData(null)}
      >
        <DialogContent className="vai-max-w-2xl vai-max-h-[80vh] vai-overflow-auto">
          <DialogHeader>
            <DialogTitle>
              <Trans>Dev Info - Mismatches</Trans>
            </DialogTitle>
          </DialogHeader>
          <pre className="vai-bg-gray-100 vai-p-4 vai-rounded vai-text-xs vai-overflow-auto vai-max-h-[60vh]">
            {JSON.stringify(devInfoModalData, null, 2)}
          </pre>
        </DialogContent>
      </Dialog>
    </>
  );
};
